/**
 * JoltSkillEffect — Lightning tree evolution: replaces Spark with a louder,
 * harder-hitting stun bolt.
 *
 * Jolt is Spark grown confident — 60% more base damage, stun stretched to
 * 600ms, but audibly loud. Where Spark was a whispered secret, Jolt is a
 * statement that announces the player to every nearby enemy.
 *
 * Upgrade inheritance: Residual Charge and Reach both carry `enhancesId:
 * "spark"`. Rather than modifying the resolver to follow a `replacedBy`
 * chain, Jolt explicitly queries the `spark` resolver scope for inherited
 * bonuses. This keeps the resolver's strict isolation intact (consistent
 * with the Punchmore precedent) while letting evolution nodes manually
 * reach back for upgrades that targeted their predecessor.
 *
 * Upgrade keys consumed via UpgradeEffectResolver:
 * - `stunDurationMs` (additive, scoped to 'spark') — +250ms from Residual Charge
 * - `damageMultiplier` (multiplicative, scoped to 'spark') — ×1.2 from Residual Charge
 * - `rangeTiles` (additive, scoped to 'spark') — +0.6 from Reach
 *
 * Fizzle behavior: identical to Spark — fizzle consumes cooldown, out-of-range
 * does not. Fizzle check uses 'jolt' skillId for independent mastery tracking.
 *
 * CC hierarchy: stun application checks `isImmune('stun')` before calling apply.
 * Damage always lands regardless of stun immunity.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see lightning-spec.md § Jolt
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface JoltDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
      stacks: number;
    }): void;
    /** Check CC immunity before applying stun (300ms window post-expiry). */
    isImmune(effectId: string): boolean;
  };
  /** Returns entity ID of nearest enemy within range, or null if none. */
  readonly getTarget: (rangeTiles: number) => string | null;
  /** Returns true if the skill activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Break stealth/hide on activation. */
  readonly breakHide: () => void;
  /** Deal direct (non-DoT) hit damage to a single target. */
  readonly dealDamage: (targetId: string, damage: number) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface JoltConfig {
  /** Cooldown in ms after activation or fizzle. Default: 400 (fast tier). */
  readonly cooldownMs: number;
  /** Base direct damage: Spark 7 × 1.6 = 11.2. */
  readonly baseDamage: number;
  /** Base stun duration in ms. Default: 600 (up from Spark's 350). */
  readonly baseStunDurationMs: number;
  /** Base bolt range in tiles. Default: 2 (same as Spark). */
  readonly baseRangeTiles: number;
}

const DEFAULT_CONFIG: JoltConfig = {
  cooldownMs: 400,           // fast cooldown tier (same as Spark)
  baseDamage: 11.2,          // 7 × 1.6 — 60% more than Spark
  baseStunDurationMs: 600,   // 600ms base stun (up from Spark 350ms)
  baseRangeTiles: 2,         // ~2 tiles short range
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class JoltSkillEffect implements SkillEffect {
  readonly skillId = 'jolt';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'WHIFF';
  readonly failLabel = 'FIZZLE';

  private readonly deps: JoltDeps;
  private readonly config: JoltConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _outOfRangeAttempts = 0;

  constructor(deps: JoltDeps, config: Partial<JoltConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Observable state ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get outOfRangeAttempts(): number { return this._outOfRangeAttempts; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve range: base + inherited Reach bonus (scoped to 'spark')
    const bonusRange = this.deps.resolver.getAdditiveEffect('spark', 'rangeTiles');
    const resolvedRange = this.config.baseRangeTiles + bonusRange;

    // Range check — no target in range = no cooldown consumed
    const targetId = this.deps.getTarget(resolvedRange);
    if (targetId === null) {
      this._outOfRangeAttempts++;
      return false;
    }

    // Fizzle check — uses 'jolt' skillId for independent mastery tracking
    if (!this.deps.rollSkillSuccess('jolt')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Break hide on successful activation
    this.deps.breakHide();

    // Resolve damage: Jolt base × inherited Residual Charge multiplier
    const damageMultiplier = this.deps.resolver.getMultiplicativeEffect('spark', 'damageMultiplier');
    const resolvedDamage = this.config.baseDamage * damageMultiplier;

    // Deal direct damage (always lands, regardless of stun immunity)
    this.deps.dealDamage(targetId, resolvedDamage);

    // Resolve stun duration: Jolt base + inherited Residual Charge bonus
    const bonusStunMs = this.deps.resolver.getAdditiveEffect('spark', 'stunDurationMs');
    const resolvedStunMs = this.config.baseStunDurationMs + bonusStunMs;

    // Apply stun — respect CC immunity window (300ms post-expiry)
    if (!this.deps.statusEffects.isImmune('stun')) {
      this.deps.statusEffects.apply('stun', {
        sourceSkillId: 'jolt',
        durationMs: resolvedStunMs,
        intensity: 1,
        stacks: 1,
      });
    }

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
